import { actionEsOnMessage, wsEsReducer, wsEsActions } from '@skynet/data-ws-es';
import { parseResponse } from '@skynet/data-ws-core';
import { startAppListening } from 'src/store/middleware/listenerMiddleware';
import { skyRouter } from 'src/routes/sky-router';
import { ApplicationDomain } from 'src/routes/types';
import { TradingApiCommands } from 'src/store/trading/apiKeys/types';
import {
  selectConnectedApiKeys,
  selectDefaultConnectedApiKeys,
  selectSupportedExchanges,
  selectTargetAccount,
} from 'src/store/trading/apiKeys/selectors';
import { selectAccessToken } from '@skynet/data-auth-api';
import { isApplicationDomain, isNoIdSection } from 'src/hooks/useSection';
import { tradingApiKeysActions } from './apiKeys/slice';
import { selectDefaultExchange } from 'src/store/trading/bots/selectors';
import { routerHistoryChange } from 'src/store/router/actions';
import { checkTradingAddApiKeyAccess } from '@skynet/authz-es-api-keys';
import { selectDefaultTrader, selectTraders } from 'src/store/trading/traders/selectors';
import { tradesActions } from 'src/store/trading/traders/slice';
import { TradingTradersCommands } from 'src/store/trading/traders/types';
import { tradingTableOptsActions } from 'src/store/trading/balance-table-opts/slice';
import type { WsEsState } from '@skynet/data-ws-es';
import { ES_BOOTSTRAP_SECTION } from 'src/store/trading/listen-sections';

export type { WsEsState };
export { wsEsActions };
export default wsEsReducer;

startAppListening({
  actionCreator: actionEsOnMessage,
  effect: async (action, listenerApi) => {
    const domain = skyRouter.getDomain();
    const section = skyRouter.getSection();
    const entityId = skyRouter.getEntityId() ?? '';

    if (!domain || !section || domain !== ApplicationDomain.Trading) {
      return;
    }

    const state = listenerApi.getState();

    const requiredCommands = [
      TradingApiCommands.Trader,
      TradingApiCommands.SupportedExchanges,
      TradingTradersCommands.Traders,
    ];
    const { json, skipProcessing } = parseResponse(action.payload, requiredCommands);

    if (skipProcessing || !json) {
      return;
    }

    const hasKeys = selectConnectedApiKeys(state).length > 0;
    const hasExchange = selectSupportedExchanges(state).length > 0;
    const hasTraders = selectTraders(state).length > 0;

    //TODO: template solution for redirecting to default entity if no entityId in url
    if (['balances', 'reports'].includes(section) && hasTraders) {
      if (!entityId) {
        const defaultTrader = selectDefaultTrader(state);
        if (defaultTrader) {
          listenerApi.dispatch(tradesActions.setTargetTrader({ tradingId: `${defaultTrader.id}` }));
          await skyRouter.router?.navigate(
            `/${domain.toLowerCase()}/${section}/${defaultTrader.id}`,
          );
        }
        return;
      } else {
        const traders = selectTraders(state);
        const selectedTrader = traders.find((tr) => tr.id === +entityId);
        if (!selectedTrader) {
          const defaultTrader = selectDefaultTrader(state);
          if (defaultTrader) {
            listenerApi.dispatch(
              tradesActions.setTargetTrader({ tradingId: `${defaultTrader.id}` }),
            );
            await skyRouter.router?.navigate(
              `/${domain.toLowerCase()}/${section}/${defaultTrader.id}`,
            );
          } else {
            await skyRouter.router?.navigate(`/${domain.toLowerCase()}/not-found`);
          }
        } else {
          listenerApi.dispatch(
            tradesActions.setTargetTrader({ tradingId: `${selectedTrader.id}` }),
          );
        }
        return;
      }
    }

    if ((!entityId || entityId === 'new') && section === ES_BOOTSTRAP_SECTION) {
      const accessToken = selectAccessToken(state);
      // TODO: this check is repeating every minute, b/c of how often backend
      // respond with Investor command. So can we optimize it?
      const { canAddApiKey } = await checkTradingAddApiKeyAccess({
        accessToken,
        scope: ApplicationDomain.Trading,
      });

      if (!hasKeys && !canAddApiKey) {
        await skyRouter.router?.navigate(`/${domain.toLowerCase()}/not-found`);
        return;
      }

      if (!hasKeys && canAddApiKey) {
        listenerApi.dispatch(tradingApiKeysActions.setSelectedApiKey('new'));
        await skyRouter.router?.navigate(`/${domain.toLowerCase()}/tasks/new`);
        return;
      }
    }

    if ((hasKeys && section === ES_BOOTSTRAP_SECTION) || (section === 'bots' && hasExchange)) {
      const defaultKey = selectDefaultConnectedApiKeys(state);
      const defaultExchanges = selectDefaultExchange(state);

      if (isApplicationDomain(domain) && isNoIdSection(domain, section)) {
        if (defaultKey) {
          listenerApi.dispatch(tradingApiKeysActions.setSelectedApiKey(String(defaultKey)));
        }
        return;
      }

      if (entityId && entityId !== 'new') {
        const activeKeys = selectConnectedApiKeys(state);
        const hasExchangeAccess = selectSupportedExchanges(state);
        const hasApiAccess = activeKeys.find((key) => String(key.accountID) === entityId);

        if (
          (section === ES_BOOTSTRAP_SECTION && !hasApiAccess) ||
          (section === 'bots' && !hasExchangeAccess.includes(+entityId))
        ) {
          listenerApi.dispatch(
            tradingApiKeysActions.setSelectedApiKey(String(defaultKey ?? 'new')),
          );
          await skyRouter.router?.navigate(`/${domain.toLowerCase()}/not-found`);
        }

        return;
      }

      if (!entityId) {
        let path = defaultKey;
        if (section === 'bots' && defaultExchanges) {
          path = defaultExchanges;
        }
        if (path) {
          await skyRouter.router?.navigate(`/${domain.toLowerCase()}/${section}/${path}`);
        }
        return;
      }
    }
  },
});

startAppListening({
  actionCreator: routerHistoryChange,
  effect: (action, listenerApi) => {
    const state = listenerApi.getState();
    const entityId = skyRouter.getEntityId() ?? '';
    const section = skyRouter.getSection();
    const domain = skyRouter.getDomain();
    const currentAccount = selectTargetAccount(state);

    if (domain !== ApplicationDomain.Trading || !section || !entityId) {
      return;
    }

    if (entityId !== 'new' && entityId !== currentAccount) {
      if (section === ES_BOOTSTRAP_SECTION) {
        const activeKeys = selectConnectedApiKeys(state);
        const selectedKey = activeKeys.find((key) => String(key.accountID) === entityId);
        if (selectedKey) {
          listenerApi.dispatch(tradingApiKeysActions.setSelectedApiKey(`${selectedKey.accountID}`));
        }
      }
      if (section === 'bots') {
        const exchanges = selectSupportedExchanges(state);
        const selectedExchange = exchanges.find((ex) => ex === +entityId);
        if (selectedExchange) {
          listenerApi.dispatch(tradingApiKeysActions.setSelectedApiKey(`${selectedExchange}`));
        }
      }
    }
    if (section === 'balances') {
      const traders = selectTraders(state);
      const selectedTrader = traders.find((tr) => tr.id === +entityId);
      if (selectedTrader) {
        listenerApi.dispatch(tradesActions.setTargetTrader({ tradingId: `${selectedTrader.id}` }));
      }
    }
  },
});

// Reset the token filter selection when switching traders so it does not leak
// across traders, matching the loans/retainers domains (they share the same
// `ignoreTokens`/`isIgnoreOtherTokens` state via the balance-table-opts factory).
startAppListening({
  actionCreator: tradesActions.setTargetTrader,
  effect: (_, listenerApi) => {
    listenerApi.dispatch(tradingTableOptsActions.resetIgnoreTokens());
  },
});
